/**
 * KB-retrieval layer — Phase 1.
 *
 * The onboarding flow's philosophy + curriculum derivation needs to be
 * grounded in Ben Petty's actual coaching guidance, not made-up examples
 * in the system prompt. This module exports the smallest viable retrieval
 * layer:
 *
 *   - 6 distilled markdown snippets, each derived from the KB sources at
 *     `KnowledgeBase/extracted/ben-voice-notes-*` and the saved spec.
 *   - A rule-based selector (`getRelevantSnippets`) that picks 2–5
 *     relevant chunks based on the coach's profile + the current stage.
 *
 * Phase 2 (separate PR) swaps the rule-based selector for proper vector
 * search via pgvector + Voyage embeddings. The call signature stays
 * stable so call sites don't change.
 *
 * Snippets are inline string constants rather than separate .md files so
 * they bundle reliably across Vite + Vitest + Node without needing
 * `?raw` plugin config.
 */

import type { PhaseOfPlay } from '../phaseOfPlay';

export type KbTopicId =
  | 'mesocycle_principle'
  | 'grassroots_session_design'
  | 'defending_pedagogy'
  | 'struggling_player_playbook'
  | 'five_common_grassroots_mistakes'
  | 'individual_vs_team_coaching';

export interface KbSnippet {
  topic: KbTopicId;
  /** Where this distilled chunk came from. Helps the prompt cite Ben
   *  rather than treating it as Brain's own opinion. */
  source: string;
  /** Bullet-point or short-prose content suitable for injection into
   *  a Sonnet/Opus system prompt context payload. */
  content: string;
}

// ---------------------------------------------------------------------------
// Snippets — distilled from KnowledgeBase/extracted/* summaries. Each
// is written to read coherently inside a prompt context block: short,
// punchy, no markdown headings, citing Ben directly.
// ---------------------------------------------------------------------------

const MESOCYCLE_PRINCIPLE: KbSnippet = {
  topic: 'mesocycle_principle',
  source: 'Ben Petty voice note, 2026-05-20, §5 (4–6 week mesocycle)',
  content: `Stay on ONE subject for 4–6 weeks. Don't flip subject-to-subject — Ben's words: "If one week you work on defending and the next week attacking and then back to defending, it all becomes very mixed and it's very, very hard to see if any learning is going on." Pick the subject (defending, build-up, transitions, individual technique), commit, and ride it across the whole cycle. The coach uses the mesocycle as scaffolding — the kids don't need to know the theme by name; do it almost silently. Variety comes from sub-themes inside the cycle (drill changes, SSG rule changes, player groupings), NOT from changing the theme. After the cycle the coach can move on to a new mesocycle on a different subject. Implications: rating velocity calculations reset their baseline at mesocycle boundaries; "what's actually moved in three weeks?" beats "what did we work on this week?".`,
};

const GRASSROOTS_SESSION_DESIGN: KbSnippet = {
  topic: 'grassroots_session_design',
  source: 'Ben Petty voice note, 2026-05-05 (grassroots layer)',
  content: `Grassroots sessions are 60–90 minutes total, made of 10–15 minute blocks. Short and sharp — grassroots-age concentration windows are short. Sessions are sequences of these short blocks, not one long expanding activity. The ball is rolling for 90% of the session — coach talking time minimised, interventions pre-planned and concise, single-cue not multi-stage. Warm-ups and skill practices default to a ball each (fallback: ball between two) — never long queues for one ball. Drills are simple, fun, and oriented toward ball mastery: touches, manipulation, basic passing quality. Area sizes drive ACTIVITY — all players involved most of the time, all physically moving (running, deciding, recovering) most of the time. Static drills and long queues are penalised. The dominant teaching unit is small-sided games with rules — the rules drive the desired outcome (e.g. "ball must reach the wide channel before you can score" produces switching play without the coach saying "switch the play"). 1v1 emphasis is the main thing — create as many 1v1 situations as possible.`,
};

const DEFENDING_PEDAGOGY: KbSnippet = {
  topic: 'defending_pedagogy',
  source: 'Ben Petty voice note, 2026-05-20, §3 (defending pedagogy)',
  content: `Defending is Ben's favourite subject to coach — "kids want to defend, they've got so much energy." Three canonical coaching points, in order: 1) Press the ball — nearest-man press, simple. 2) Protect the middle of the pitch and show outside — defensive direction. 3) Help your mate — if your mate is getting attacked, slips over, makes a mistake, or the opponent goes past, are you in the right position to help? Coach body shape, NOT tackling: side-on, low, balls of feet. Best defenders don't tackle — positioning + being hard to beat does most of the work. Tackling can be coached but should NOT be the headline lesson. Practice format: lots of small-sided games, lots of 1v1 / 2v2 / 3v2 / 2v3 overloaded and underloaded. "Opportunities to practice not only getting success but failing as well" — failure is part of learning at this tier. When the coach picks defending as a focus, drill mix defaults to 1v1 / 2v2 / 3v2 / 2v3 SSG variants with these three points as the principles. Body-shape cues sit as coaching points on each drill card. Tackling drills are de-prioritised.`,
};

const STRUGGLING_PLAYER_PLAYBOOK: KbSnippet = {
  topic: 'struggling_player_playbook',
  source: 'Ben Petty voice note, 2026-05-20, §4 (handling the struggling player)',
  content: `When a player isn't getting it, follow this sequence: 1) Simplify the drill — more touches, easier constraint, or re-demonstrate. 2) Take the player aside and ASK them what they're thinking — not telling. Gauge understanding: "what part of the session don't you understand?" 3) Build trust — the player has to trust that they won't be isolated every time they get something wrong. Without trust, no success. With trust, success follows. Ben's exact words: "I think the more that the player trusts the coach that he's not going to be isolated every time he gets something wrong, I think this is when you'll get success." When a player's corner ratings are stagnant or velocity-dropping, the right Brain prompt to the coach is "have you stood with them after a drill yet?" — action-first, not analysis-first. Propose a one-to-one conversation, NOT a programme of correction.`,
};

const FIVE_COMMON_GRASSROOTS_MISTAKES: KbSnippet = {
  topic: 'five_common_grassroots_mistakes',
  source: 'Ben Petty voice note, 2026-05-20, §1 (five most common grassroots mistakes)',
  content: `Ben's ranked list of the five things grassroots coaches most commonly get wrong: 1) No clear objective — sessions go ahead without a stated theme. Every session needs an objective, even if as basic as "we're focused on defending today" or "we're focused on attacking today". 2) Too much coach talking, not enough play — target is 90% ball rolling across the 60–90 minutes. Interventions must be planned and concise, not rambling. Single-cue, deliverable inside the game. 3) Drills don't look like the game — endless passing drills and dribbling round cones are "multi-skills, for juniors", not grassroots coaching. The dominant unit at grassroots is small-sided games with goals / targets / phases of play. 4) One-size-fits-all session designed around the core/best players — leaving weaker players disengaged or unchallenged players bored. Sessions must target weaker players too, not just the core. 5) Poor organisation — sessions not set up before training starts, water breaks not planned, coach scrambling between activities. Set up the rest of the session during the warm-up if you can.`,
};

const INDIVIDUAL_VS_TEAM_COACHING: KbSnippet = {
  topic: 'individual_vs_team_coaching',
  source: 'Ben Petty voice note, 2026-05-20, §2 (good vs bad in-the-moment coaching points)',
  content: `At grassroots, coach the INDIVIDUAL — not the team. Skill variance is huge at this level; each player has different gaps. Brain's job at grassroots is making each individual better. "If each individual becomes better, the team becomes stronger" — not the other way around. Good coaching: helps the individual player; helps the player see and solve the problem themselves (questioning, not instructing); lots of one-to-one interactions. Bad coaching at grassroots: treating the team as the unit, "team talks", coaching positional units. That's academy work, not grassroots. Coaching-points generation at grassroots should default to named-player prompts ("Sam — what could you do here?") rather than team-addressed ("everyone, what could we do here?"). The mobile coach screens should surface INDIVIDUAL coaching prompts during sessions, not team announcements. The job is making 14 individuals better, not orchestrating 14 positions.`,
};

export const SNIPPETS: Record<KbTopicId, KbSnippet> = {
  mesocycle_principle: MESOCYCLE_PRINCIPLE,
  grassroots_session_design: GRASSROOTS_SESSION_DESIGN,
  defending_pedagogy: DEFENDING_PEDAGOGY,
  struggling_player_playbook: STRUGGLING_PLAYER_PLAYBOOK,
  five_common_grassroots_mistakes: FIVE_COMMON_GRASSROOTS_MISTAKES,
  individual_vs_team_coaching: INDIVIDUAL_VS_TEAM_COACHING,
};

// ---------------------------------------------------------------------------
// Selector — picks the relevant snippets given a coach profile shape +
// the current onboarding stage. Rule-based, deterministic. Phase 2
// swaps this for a vector search but keeps the same signature.
// ---------------------------------------------------------------------------

/** Shape of the profile fragment the selector reads. Decoupled from the
 *  full OnboardingProfile so this module stays in @4cm/core (no app deps). */
export interface KbSelectorProfile {
  coachLevel?: string | null;
  ageGroup?: string | null;
  methodology?: string | null;
  focus?: PhaseOfPlay | null;
}

/** Stage names the selector recognises. Mirrors OnboardingStage from the
 *  web2 app but kept loose so core doesn't depend on the app's types. */
export type KbSelectorStage =
  | 'coach' | 'team' | 'roster' | 'season' | 'aims' | 'schedule' | 'plan'
  | 'philosophy' | 'curriculum' | 'confirm' | 'walkthrough' | 'done';

/** Brain surfaces that pull KB context. Each surface has its own snippet
 *  cap because per-turn budgets differ: a long derivation can carry 4
 *  chunks, a 2-sentence reply can't.
 *
 *  Names here mirror packages/core/src/aiSurfaces.ts where possible, but
 *  we sub-divide a single AI surface into its functional purposes
 *  ('voice-onboarding-extract' vs 'voice-onboarding-derive') so the cap
 *  reflects the actual call shape, not just the LLM tier. */
export type KbSurface =
  | 'voice-onboarding-extract'
  | 'voice-onboarding-save'
  | 'voice-onboarding-derive';

/** Per-surface snippet caps. Default `MAX_SNIPPETS` (4) covers high-budget
 *  generations like derive-philosophy. Brief conversational surfaces get
 *  2 so the kb_context payload doesn't dwarf the reply itself.
 *
 *  Approx token cost: each snippet is ~400 tokens of content + ~30
 *  tokens of source/topic framing — 4 snippets ≈ 1.7k tokens, 2 ≈ 0.9k.
 */
const MAX_SNIPPETS = 4;
const MAX_SNIPPETS_BY_SURFACE: Record<KbSurface, number> = {
  'voice-onboarding-derive': 4,
  'voice-onboarding-extract': 2,
  'voice-onboarding-save': 2,
};

/** Surfaces explicitly EXCLUDED from KB injection. The exclusion list is
 *  documented in code so a future reader sees the reasoning, not just an
 *  absence. Add to this list (with rationale) when a new Brain surface is
 *  audited and judged not to benefit from coaching-voice-notes context.
 *
 *  Rule of thumb for excluding a surface: the AI call doesn't reason
 *  about football — it does mechanical parsing, classification, or
 *  vision/audio I/O.
 */
export const EXCLUDED_SURFACES = {
  'video-classify':         'Vision classifier — labels frames against a fixed taxonomy; Ben\'s voice notes don\'t change "is this a goal" or "is this a shot on target".',
  'video-action-spotting':  'Per-frame event detection from clip metadata; doesn\'t reason about coaching philosophy.',
  'ai-capture':             'Photo-to-session-plan OCR + structure extraction; KB context biases the extractor away from the literal whiteboard contents.',
  'drill-prompt-parser':    'Parses a coach\'s typed/spoken drill prompt into structured fields — mechanical, not reasoning.',
  'drill-voice':            'Voice-to-drill transcription wrapper — STT cleanup, not reasoning.',
  'voice-canvas':           'In-session voice commands ("add a token here") — UI driver, not reasoning.',
  'voice-drill':            'Voice-to-drill-creation — same as drill-voice.',
} as const;


const GRASSROOTS_TIERS = new Set(['NOVICE', 'GRASSROOTS']);
const U7_U12 = new Set(['U7', 'U8', 'U9', 'U10', 'U11', 'U12']);

/** Returns the snippets most relevant to the coach + current stage.
 *  Ordered roughly by relevance (most relevant first) — callers can
 *  rely on the first 1-2 being the highest-signal pick.
 *
 *  Pass `surface` to get the surface-specific cap (e.g. extract calls
 *  cap at 2 snippets because the reply is 1-2 sentences). Omit it and
 *  the default `MAX_SNIPPETS` (4) applies — used by derive-philosophy
 *  and any non-onboarding caller until they're audited. */
export function getRelevantSnippets(
  profile: KbSelectorProfile,
  stage: KbSelectorStage,
  surface?: KbSurface,
): KbSnippet[] {
  const isGrassroots = profile.coachLevel
    ? GRASSROOTS_TIERS.has(profile.coachLevel)
    : true; // default assumption for unknown level

  const isU7_U12 = profile.ageGroup ? U7_U12.has(profile.ageGroup) : false;

  const methodologyText = (profile.methodology ?? '').toLowerCase();
  const isDefendingFocus =
    profile.focus === 'defending' ||
    /\b(defend|compact|shape|hard to beat|press|low block|mid[- ]block)\b/.test(methodologyText);

  const picks: KbSnippet[] = [];

  // Stage gating — most relevant snippets per stage.
  switch (stage) {
    case 'philosophy':
    case 'curriculum': {
      // Both review stages share the same context — these stages are where
      // the AI actually generates the philosophy + curriculum, so they need
      // all the relevant guidance.
      picks.push(SNIPPETS.mesocycle_principle);
      if (isGrassroots) picks.push(SNIPPETS.grassroots_session_design);
      if (isDefendingFocus) picks.push(SNIPPETS.defending_pedagogy);
      if (isGrassroots && isU7_U12) picks.push(SNIPPETS.individual_vs_team_coaching);
      if (isGrassroots) picks.push(SNIPPETS.five_common_grassroots_mistakes);
      break;
    }
    case 'aims':
    case 'plan': {
      // The mesocycle principle steers what the coach commits to. Brain
      // pushes back on multi-theme requests using this context.
      picks.push(SNIPPETS.mesocycle_principle);
      if (isGrassroots) picks.push(SNIPPETS.grassroots_session_design);
      break;
    }
    case 'confirm':
    case 'walkthrough':
    case 'done': {
      // Sets the tone for the save-success reply — the coach is about to
      // start the actual work, so Brain leans into trust-building +
      // struggling-player guidance.
      picks.push(SNIPPETS.struggling_player_playbook);
      if (isGrassroots) picks.push(SNIPPETS.individual_vs_team_coaching);
      break;
    }
    case 'coach':
    case 'team':
    case 'roster':
    case 'season':
    case 'schedule':
      // Rapport / fact-gathering stages — no KB injection needed; the
      // conversation is about the coach not the curriculum.
      break;
  }

  const cap = surface ? MAX_SNIPPETS_BY_SURFACE[surface] : MAX_SNIPPETS;
  return picks.slice(0, cap);
}
